import * as React from 'react';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import {faTrashAlt} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Form, Formik, FormikProps} from 'formik';
import {useEffect, useRef, useState} from 'react';

import Button from '../ui/Buttons/Button';
import {Alerts} from '../ui/Alerts/Alerts';
import {capitalize} from '../../client/ui/utils/formatting';
import {ContentModal} from '../ui/modals/ContentModal/ContentModal';
import {InputFormik} from '../ui/forms/Input/InputFormik';
import {Recaptcha} from '../recaptcha/Recaptcha';
import {ShareItemRes} from '../items/item.class';
import {ShareLinkParams, ShareLinkType} from './share-link.class';
import {ShareListRes} from '../lists/list.class';
import {TextAreaFormik} from '../ui/forms/TextArea/TextAreaFormik';
import {ToolTip} from '../ui/Tooltip/Tooltip';
import {User} from '../users/user.class';

interface ShareLinkModalProps {
    disableRecaptcha: boolean;
    linkId: string;
    onClose: () => void;
    onShareLink: (shareLinkParams: ShareLinkParams) => void;
    shareLinkErr: string;
    shareLinkModalTitle?: string;
    shareLinkRes: ShareListRes | ShareItemRes;
    shareLinkShowForm: boolean;
    shareLinkShowSuccess: boolean;
    shareLinkShowWarning: boolean;
    shareLinkType: ShareLinkType;
    show: boolean;
    singleName?: boolean;
    user: User;
}

export const ShareLinkModal = ({
    disableRecaptcha,
    linkId,
    onClose,
    onShareLink,
    shareLinkErr,
    shareLinkModalTitle,
    shareLinkRes,
    shareLinkShowForm,
    shareLinkShowSuccess,
    shareLinkShowWarning,
    shareLinkType,
    show,
    singleName,
    user,
}: ShareLinkModalProps) => {
    const [hideShareLinkErr, setHideShareLinkErr] = useState(false);
    const formRef = useRef<FormikProps<ShareLinkParams>>();
    const recaptchaRef = useRef<ReCAPTCHA>();
    const shareLinkTypeCap = capitalize(shareLinkType);

    // Reset UI state on shareLinkErr
    useEffect(() => {
        setHideShareLinkErr(false);
        recaptchaRef?.current?.reset();
    }, [shareLinkErr]);

    /**
     * TBD
     */
    const addFormFields = () => {
        formRef.current
            .setFieldValue(`recipients`, [
                ...formRef.current.values.recipients,
                {
                    rFirstname: ``,
                    rLastname: ``,
                    rEmail: ``,
                },
            ])
            .catch((setFieldValueErr) => {
                alert(setFieldValueErr.message);
            });
    };

    /**
     * Returns validation schema based on name format
     */
    const getFormValidation = (): yup.ObjectSchema<any> => {
        if (singleName) {
            return yup.object().shape({
                sEmail: yup.string().required(`Your Email is required`),
                sName: yup.string().required(`Your Name is required`),
                recipients: yup.array(
                    yup.object().shape({
                        rEmail: yup.string().required(`Recipient Email is required`),
                        rName: yup.string().required(`Recipient Name is required`),
                    }),
                ),
            });
        } else {
            return yup.object().shape({
                sEmail: yup.string().required(`Your Email is required`),
                sLastname: yup.string().required(`Your Last Name is required`),
                sFirstname: yup.string().required(`Your First Name is required`),
                recipients: yup.array(
                    yup.object().shape({
                        rEmail: yup.string().required(`Recipient Email is required`),
                        rFirstname: yup.string().required(`Recipient First Name is required`),
                        rLastname: yup.string().required(`Recipient Last Name is required`),
                    }),
                ),
            });
        }
    };

    /**
     * Returns initial values based on name format
     */
    const getRecipientInitialValues = () => {
        if (singleName) {
            return [{rEmail: ``, rName: ``}];
        } else {
            return [{rFirstname: ``, rLastname: ``, rEmail: ``}];
        }
    };

    /**
     * remove fields from formValues
     * @param i
     */
    const removeFormFields = (i) => {
        const newFormValues = [...formRef.current.values.recipients];
        newFormValues.splice(i, 1);
        formRef.current.setFieldValue(`recipients`, newFormValues).catch((setFieldValueErr) => alert(setFieldValueErr.message));
    };

    /**
     * handler for form submit event
     * @param shareLinkParams
     */
    const handleSubmit = async (shareLinkParams: ShareLinkParams) => {
        shareLinkParams.type = shareLinkType;
        setHideShareLinkErr(true);

        // If not disabled, add recaptcha to params
        if (!disableRecaptcha) {
            shareLinkParams[`g-recaptcha-response`] = await recaptchaRef.current.executeAsync();
        }

        // Add shareLinkType specific params
        if (shareLinkType === `item`) {
            shareLinkParams.itemNum = linkId;
        }
        if (shareLinkType === `items`) {
            shareLinkParams.listId = linkId;
            shareLinkParams.loginName = user.loginName;
        }
        if (shareLinkType === `list`) {
            shareLinkParams.listId = linkId;
        }

        // Perform shareLinkAction
        onShareLink(shareLinkParams);
    };

    /**
     * Template
     */
    return (
        <ContentModal
            show={show}
            title={shareLinkModalTitle ? shareLinkModalTitle : `Share ${shareLinkTypeCap}`}
            footer={
                <>
                    <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-w-full">
                        <div className="tw-w-full tw-relative tw-text-left tw-px-0">
                            <Button
                                className="!tw-w-full lg:!tw-w-auto !tw-mb-4 lg:!tw-mb-0"
                                modal={true}
                                onClick={() => location.assign('/content/privacy')}
                                size="smToLg"
                                variant="outline-secondary"
                            >
                                View Privacy Policy
                            </Button>
                        </div>
                        {shareLinkShowForm && (
                            <div className="tw-w-full tw-relative tw-text-right tw-px-0">
                                <Button
                                    className="!tw-w-full lg:!tw-w-auto !tw-mb-4 lg:!tw-mb-0 !tw-mr-0 lg:!tw-mr-4"
                                    modal={true}
                                    onClick={onClose}
                                    size="smToLg"
                                    variant="outline-secondary"
                                    type="button"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    modal={true}
                                    onClick={() => {
                                        formRef.current.submitForm().catch((e) => {
                                            alert(e.message);
                                        });
                                    }}
                                    size="smToLg"
                                    variant="secondary"
                                    type="button"
                                >
                                    Share {shareLinkTypeCap}
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            }
            footerStyleCustom={true}
            onClose={onClose}
        >
            <Formik
                initialValues={{
                    [`g-recaptcha-response`]: ``,
                    comments: ``,
                    recipients: getRecipientInitialValues(),
                    sEmail: user.loginName,
                    sFirstname: ``,
                    sLastname: ``,
                    sName: singleName && user.fullName ? user.fullName : ``,
                    type: shareLinkType,
                }}
                innerRef={formRef}
                onSubmit={handleSubmit}
                validationSchema={getFormValidation()}
            >
                {({values}) => (
                    <Form className="tw-w-full">
                        {!hideShareLinkErr && shareLinkErr && (
                            <Alerts
                                message={shareLinkErr}
                                multiLine={true}
                                variant="danger"
                            />
                        )}
                        {shareLinkShowForm && (
                            <>
                                <div className="tw-text-sm tw-font-normal tw-leading-[1.3] tw-text-right">* required</div>
                                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-0 lg:tw-gap-5">
                                    {singleName && (
                                        <div className="tw-w-full tw-relative tw-col-span-1 lg:tw-col-span-3">
                                            <InputFormik
                                                className="maskPII"
                                                label="Name"
                                                name="sName"
                                                autocorrect="off"
                                            />
                                        </div>
                                    )}
                                    {!singleName && (
                                        <>
                                            <div className="tw-w-full tw-relative tw-col-span-1 lg:tw-col-span-3">
                                                <InputFormik
                                                    className="maskPII"
                                                    label="Your First Name"
                                                    name="sFirstname"
                                                    autocorrect="off"
                                                />
                                            </div>
                                            <div className="tw-w-full tw-relative tw-col-span-1 lg:tw-col-span-3">
                                                <InputFormik
                                                    className="maskPII"
                                                    label="Your Last Name"
                                                    name="sLastname"
                                                    autocorrect="off"
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div className="tw-w-full tw-relative tw-col-span-1 lg:tw-col-span-5">
                                        <InputFormik
                                            className="maskPII"
                                            label="Your Email"
                                            name="sEmail"
                                            autocorrect="off"
                                        />
                                    </div>
                                </div>
                                {values.recipients.map((element, index) => (
                                    <div
                                        className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-0 lg:tw-gap-5"
                                        key={index}
                                    >
                                        {singleName && (
                                            <div className="tw-w-full tw-relative tw-col-span-1 lg:tw-col-span-3">
                                                <InputFormik
                                                    className="maskPII"
                                                    label="Recipient Name"
                                                    name={`recipients.${index}.rName`}
                                                    required
                                                />
                                            </div>
                                        )}
                                        {!singleName && (
                                            <>
                                                <div className="tw-w-full tw-relative tw-col-span-1 lg:tw-col-span-3">
                                                    <InputFormik
                                                        className="maskPII"
                                                        label="Recipient First Name"
                                                        name={`recipients.${index}.rFirstname`}
                                                        required
                                                    />
                                                </div>
                                                <div className="tw-w-full tw-relative tw-col-span-1 lg:tw-col-span-3">
                                                    <InputFormik
                                                        className="maskPII"
                                                        label="Recipient Last Name"
                                                        name={`recipients.${index}.rLastname`}
                                                        required
                                                    />
                                                </div>
                                            </>
                                        )}
                                        <div className="tw-w-full tw-relative tw-col-span-1 lg:tw-col-span-5">
                                            <InputFormik
                                                className="maskPII"
                                                label="Recipient Email"
                                                name={`recipients.${index}.rEmail`}
                                                required
                                            />
                                        </div>
                                        <div className="tw-w-full tw-relative tw-col-span-1">
                                            {index ? (
                                                <div className="tw-pt-2 tw-mt-1">
                                                    <ToolTip
                                                        id={`RemoveFields${index}`}
                                                        title="Remove Fields"
                                                    >
                                                        <FontAwesomeIcon
                                                            className="tw-cursor-pointer"
                                                            icon={faTrashAlt}
                                                            onClick={() => removeFormFields(index)}
                                                        />
                                                    </ToolTip>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                                <div className="tw-text-left tw-justify-start">
                                    <div className="tw-mb-4">
                                        <Button
                                            className="tw-pl-0 !tw-py-2"
                                            onClick={addFormFields}
                                            size="sm"
                                            type="button"
                                            variant="link"
                                        >
                                            Add Another Recipient
                                        </Button>
                                    </div>
                                    <TextAreaFormik
                                        name="comments"
                                        placeholder="Message (optional)"
                                    />
                                    <Recaptcha
                                        disableRecaptcha={disableRecaptcha}
                                        ref={recaptchaRef}
                                    />
                                </div>
                            </>
                        )}
                        {shareLinkShowWarning && (
                            <>
                                <div className="tw-flex tw-flex-wrap">
                                    Oops, something went wrong! We were unable to email your {shareLinkType} to the following recipients:{' '}
                                    <br />
                                    <ul style={{paddingLeft: 55}}>
                                        {shareLinkRes.msg.map((errors, index) => (
                                            <li key={index}>${errors.error}</li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        )}
                        {shareLinkShowSuccess && (
                            <>
                                <Alerts
                                    message={`Your ${shareLinkType} has been successfully sent to the following recipients:`}
                                    variant="success"
                                />
                                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-0 lg:tw-gap-5">
                                    {singleName && <div className="tw-w-full tw-relative tw-font-bold">Name</div>}
                                    {!singleName && (
                                        <>
                                            <div className="tw-w-full tw-relative tw-font-bold">First Name</div>
                                            <div className="tw-w-full tw-relative tw-font-bold">Last Name</div>
                                        </>
                                    )}
                                    <div className="tw-w-full tw-relative tw-font-bold">Email</div>
                                </div>
                                {shareLinkRes.sentTo.map((value, index) => (
                                    <div
                                        className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-0 lg:tw-gap-5"
                                        key={index}
                                    >
                                        {value.rName && <div className="tw-w-full tw-relative maskPII">{value.rName}</div>}
                                        {value.rFirstname && <div className="tw-w-full tw-relative maskPII">{value.rFirstname}</div>}
                                        {value.rLastname && <div className="tw-w-full tw-relative maskPII">{value.rLastname}</div>}
                                        {value.rEmail && <div className="tw-w-full tw-relative maskPII">{value.rEmail}</div>}
                                    </div>
                                ))}
                            </>
                        )}
                    </Form>
                )}
            </Formik>
        </ContentModal>
    );
};
